/* ============================================================================
   bsfn-subscribe.js — Subscribe & Save behaviour for the BSFN buy box and cart.

   ORIGIN     ORIGINAL — authored for BSFN against the Shopify platform APIs
              cited below, from the internal "BSFN Subscription Buy Box"
              design handoff (2026-09-03).

   REFERENCE  Selling plans in Liquid   shopify.dev/docs/api/liquid/objects/selling_plan_allocation
              Theme subscriptions guide shopify.dev/docs/storefronts/themes/pricing-payments/subscriptions/add-subscriptions-to-your-theme
              Cart AJAX API             shopify.dev/docs/api/ajax/reference/cart
                                        (/cart/change.js with `selling_plan`)

   DESIGN NOTES
     · Prices are never computed from hard-coded percentages. The buy box
       snippet serialises the live selling-plan allocations for every variant
       into a JSON block; this file only does arithmetic on those cents.
     · Bundle tiers are quantities (1 / 2 / 3 of the same variant). Their
       "save" percentage comes from the section settings and must match an
       automatic discount configured in the Shopify admin, or it is shown as 0.
     · One small state machine per buy box: tier, plan (sub | one), selling
       plan id, variant id. Every change re-renders every price on the page
       from that state — the price row, the tier cards, both option cards, the
       add-to-cart label and note, the sticky bar, and the hidden form inputs.
     · The hidden `selling_plan` input is DISABLED (not removed) for one-time
       purchases; disabled controls are not submitted, so Shopify's dynamic
       checkout button and the native form both post the right thing.
     · Copy tokens: [name] [pct] [freq] [unit] [units] [tier] [days] [servings] are replaced
       in any element carrying data-sub-copy, so the copy can live in the locale
       file (products.subscribe.*) and still show live numbers.

   PUBLIC API   window.BSFN.sub.init(root) | .dates(scope)
   EVENTS       emits 'bsfn:tier' {tier}
                emits 'bsfn:purchase_option' {plan, sellingPlanId}
                listens 'bsfn:drawer:opened' / 'bsfn:drawer:closed'
                emits 'bsfn:cart:updated' {cart, source:'switch'} after a
                  cart line is switched onto a selling plan
   ========================================================================== */
(function () {
  'use strict';
  /* Storefront root from Shopify (a locale or market prefix lives here), never a bare '/'. */
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var BSFN = (window.BSFN = window.BSFN || {});
  if (BSFN.sub) return;

  var REDUCED = false;
  try { REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}

  /* ---------- helpers ------------------------------------------------------ */

  function emit(name, detail) {
    try { document.dispatchEvent(new CustomEvent(name, { detail: detail || {} })); } catch (e) {}
  }

  function money(cents, format) {
    var value = cents / 100;
    var fmt = format || '${{amount}}';
    function withDelims(num, dec) {
      var parts = Math.abs(num).toFixed(dec).split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
      return (num < 0 ? '-' : '') + parts.join('.');
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, token) {
      switch (token) {
        case 'amount_no_decimals':                       return withDelims(value, 0);
        case 'amount_with_comma_separator':              return withDelims(value, 2).replace(/[,.]/g, function (c) { return c === ',' ? '.' : ','; });
        case 'amount_no_decimals_with_comma_separator':  return withDelims(value, 0).replace(/,/g, '.');
        case 'amount_with_apostrophe_separator':         return withDelims(value, 2).replace(/,/g, "'");
        default:                                         return withDelims(value, 2);
      }
    }).replace(/<[^>]*>/g, '');
  }

  /* "Deliver every 30 days" → "every 30 days"; "1 Month" → "every 1 month". */
  function freqPhrase(name) {
    var n = String(name || '').trim()
      .replace(/^(deliver(y|ed|s)?|ship(s|ped)?|subscribe( (and|&) save)?|renews?|recurring)\s*[:\-–]?\s*/i, '')
      .trim();
    if (!n) return '';
    if (!/^every\b/i.test(n)) n = 'every ' + n;
    return n.toLowerCase();
  }

  /* Parse "every 30 days" / "2 weeks" / "1 month" into a future Date. */
  function nextDate(planName) {
    var m = /(\d+)?\s*(day|week|month|year)/i.exec(String(planName || ''));
    if (!m) return null;
    var n = parseInt(m[1] || '1', 10);
    var unit = m[2].toLowerCase();
    var d = new Date();
    if (unit === 'day') d.setDate(d.getDate() + n);
    else if (unit === 'week') d.setDate(d.getDate() + n * 7);
    else if (unit === 'month') d.setMonth(d.getMonth() + n);
    else d.setFullYear(d.getFullYear() + n);
    return d;
  }

  function fmtDate(d) {
    try { return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
    catch (e) { return d.toDateString().slice(4, 10); }
  }

  /* Fill "Next delivery <date>" placeholders in the cart (drawer or page). */
  function dates(scope) {
    var els = (scope || document).querySelectorAll('[data-bsfn-next-delivery]');
    Array.prototype.forEach.call(els, function (el) {
      var d = nextDate(el.getAttribute('data-plan'));
      var wrap = el.closest('[data-bsfn-next-delivery-wrap]');
      if (!d) { if (wrap) wrap.hidden = true; return; }
      el.textContent = fmtDate(d);
      if (wrap) wrap.hidden = false;
    });
  }

  /* ---------- the buy box state machine ------------------------------------ */

  function initBox(box) {
    if (box.__bsfnSub) return;
    box.__bsfnSub = true;

    var cfgEl = box.querySelector('[data-bsfn-sub-config]');
    var cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
    if (!cfg || !cfg.variants) return;

    var form = box.closest('form');
    var scope = box.closest('.shopify-section') || document;
    var sticky = scope.querySelector('[data-sub-sticky]');
    var faq = scope.querySelector('[data-sub-faq]');
    var tiersOn = box.hasAttribute('data-tiers');

    /* Every render target inside this section: the box, the price row above
       it, the sticky bar and the FAQ all live in the same section element. */
    function all(sel) { return Array.prototype.slice.call(scope.querySelectorAll(sel)); }
    function setText(sel, text) { all(sel).forEach(function (el) { el.textContent = text; }); }

    var variantIds = Object.keys(cfg.variants);
    function currentVariantId() {
      if (form) {
        var inp = form.querySelector('[name="id"]:checked') || form.querySelector('select[name="id"]') || form.querySelector('input[name="id"]');
        if (inp && inp.value && cfg.variants[inp.value]) return inp.value;
      }
      return variantIds[0];
    }

    var state = {
      tier: tiersOn ? (cfg.defaultTier || 1) : 1,
      plan: (cfg.defaultPlan === 'one_time') ? 'one' : 'sub',
      planId: cfg.defaultPlanId || null,
      variantId: currentVariantId(),
      cartOpen: false,
      pastAtc: false
    };
    if (!tiersOn && form) {
      var qEl = form.querySelector('[name="quantity"]');
      if (qEl) state.tier = Math.max(1, parseInt(qEl.value, 10) || 1);
    }

    function variant() { return cfg.variants[state.variantId] || cfg.variants[variantIds[0]] || { price: 0, plans: [] }; }
    function plans() { return variant().plans || []; }
    function hasPlans() { return plans().length > 0; }
    function currentPlan() {
      var ps = plans();
      if (!ps.length) return null;
      for (var i = 0; i < ps.length; i++) if (String(ps[i].id) === String(state.planId)) return ps[i];
      return ps[0];
    }
    function isSub() { return hasPlans() && (cfg.requiresPlan || state.plan === 'sub'); }
    function planPct(p) {
      if (!p) return 0;
      if (p.pct !== null && p.pct !== undefined) return Math.round(p.pct);
      if (p.compare > 0 && p.price < p.compare) return Math.round((1 - p.price / p.compare) * 100);
      return 0;
    }
    function bundlePct(t) { return (cfg.bundle && cfg.bundle[t]) || 0; }
    /* Priced the way the cart charges it: Shopify takes the automatic bundle
       discount off each unit, rounded down to the cent, then multiplies by the
       quantity. Applying the percentage to the total instead drifts by a few
       cents ($138.21 on the page against $138.24 in the cart). */
    function bundled(unit, t) { return t * (unit - Math.floor(unit * bundlePct(t) / 100)); }
    /* Days of supply: what the card says, or units x the per-unit figure — the
       plain quantity field can ask for a number no card covers. */
    function daysFor(t) { var d = (cfg.days && cfg.days[t]) || 0; return d > 0 ? d : (cfg.supplyDays || 30) * t; }
    /* The selected card's title, as the merchant wrote it. No card covers a
       hand-typed quantity, so that falls back to the number and the unit. */
    function nameFor(t) { var n = cfg.names && cfg.names[t]; return n ? n : t + ' ' + unitWord(t); }
    function unitOne() { return variant().price || 0; }
    function unitList() { var v = variant(); return (v.compare && v.compare > v.price) ? v.compare : v.price; }
    function tierOne(t) { return bundled(unitOne(), t); }
    function tierSub(t) { var p = currentPlan(); return bundled(p ? p.price : unitOne(), t); }
    function tierList(t) { return unitList() * t; }
    function tierPrice(t) { return isSub() ? tierSub(t) : tierOne(t); }
    function fmt(c) { return money(c, cfg.moneyFormat); }
    function unitWord(n) { return n === 1 ? (cfg.unit || 'jar') : (cfg.unitPlural || 'jars'); }
    function savePct(price, list) { return list > 0 && price < list ? Math.round((1 - price / list) * 100) : 0; }

    /* copy tokens */
    function fill(el) {
      var tpl = el.getAttribute('data-tpl');
      if (tpl === null) { tpl = el.textContent; el.setAttribute('data-tpl', tpl); }
      var p = currentPlan(), t = state.tier;
      el.textContent = tpl
        .replace(/\[name\]/g, nameFor(t))
        .replace(/\[pct\]/g, planPct(p) + '%')
        .replace(/\[freq\]/g, freqPhrase(p ? p.name : ''))
        .replace(/\[units\]/g, unitWord(t))
        .replace(/\[unit\]/g, unitWord(1))
        .replace(/\[tier\]/g, String(t))
        .replace(/\[days\]/g, String(daysFor(t)))
        .replace(/\[servings\]/g, String((cfg.servings || 0) * t));
    }

    function rebuildFreq() {
      var sel = box.querySelector('[data-sub-freq]');
      if (!sel) return;
      var ps = plans(), cur = currentPlan();
      sel.innerHTML = '';
      ps.forEach(function (p) {
        var o = document.createElement('option');
        o.value = p.id; o.textContent = p.name;
        if (cur && String(cur.id) === String(p.id)) o.selected = true;
        sel.appendChild(o);
      });
    }

    function render() {
      var t = state.tier, sub = isSub(), p = currentPlan(), v = variant();
      var price = tierPrice(t), list = tierList(t), pct = planPct(p);
      var avail = v.available !== false;
      /* Resolved once: the selected variant's image, else the box's default. */
      var vimg = v.image || cfg.image;

      /* price row. priceBasis picks which figure is the headline; the other one
         always lands on the sub-line, so both stay on the page either way.
         Per-serving figures round to the nearest cent ($47.99 / 12 = $4.00), and
         the Liquid that paints them first rounds the same way. */
      var totalServings = (cfg.servings || 0) * t;
      var byServing = cfg.priceBasis === 'serving' && totalServings > 0;
      var headPrice = byServing ? Math.round(price / totalServings) : price;
      var headList = byServing ? Math.round(list / totalServings) : list;
      var perWord = cfg.labels.perServing || 'per serving';
      var manyWord = cfg.labels.servings || 'servings';

      setText('[data-price-live]', fmt(headPrice));
      all('[data-price-compare]').forEach(function (el) {
        if (headPrice < headList) { el.textContent = fmt(headList); el.hidden = false; } else { el.textContent = ''; el.hidden = true; }
      });
      if (totalServings > 0) {
        setText('[data-price-serving]', byServing
          ? perWord + ' · ' + fmt(price) + ' for ' + totalServings + ' ' + manyWord
          : fmt(Math.round(price / totalServings)) + ' ' + perWord + ' · ' + totalServings + ' ' + manyWord);
      }

      /* tier cards */
      all('[data-tier]').forEach(function (btn) {
        var n = parseInt(btn.getAttribute('data-tier'), 10);
        var tp = tierPrice(n), tl = tierList(n), sv = savePct(tp, tl);
        btn.setAttribute('aria-checked', n === t ? 'true' : 'false');
        btn.disabled = !avail;
        var pe = btn.querySelector('[data-tier-price]'); if (pe) pe.textContent = fmt(tp);
        var ce = btn.querySelector('[data-tier-compare]'); if (ce) ce.textContent = sv ? fmt(tl) : '';
        /* Per-serving off THIS card's price, so it carries the plan discount and
           the card's own buy-more tier. Rounds, like the Liquid that first paints it. */
        var ve = btn.querySelector('[data-tier-serving]');
        if (ve) {
          var tsv = (cfg.servings || 0) * n;
          ve.textContent = tsv > 0 ? fmt(Math.round(tp / tsv)) + ' ' + (cfg.labels.perServing || 'per serving') : '';
        }
        var se = btn.querySelector('[data-tier-save]');
        if (se) {
          se.textContent = sv ? (cfg.labels.save || 'Save') + ' ' + sv + '%' : (cfg.labels.standard || 'Standard price');
          se.classList.toggle('is-save', sv > 0);
        }
        if (vimg) btn.querySelectorAll('[data-tier-img]').forEach(function (im) { if (im.getAttribute('src') !== vimg) im.setAttribute('src', vimg); });
      });

      /* The sticky bar's thumbnail is the same photo of the same unit, so it
         follows the selected variant too — with a flavor rail it would
         otherwise be left showing the flavor the page was loaded on. */
      if (vimg) all('[data-sticky-thumb]').forEach(function (im) {
        if (im.getAttribute('src') !== vimg) im.setAttribute('src', vimg);
      });

      /* option cards */
      setText('[data-sub-pct]', pct + '%');
      setText('[data-sub-price]', fmt(tierSub(t)));
      all('[data-sub-compare]').forEach(function (el) { el.textContent = tierSub(t) < tierOne(t) ? fmt(tierOne(t)) : ''; });
      setText('[data-one-price]', fmt(tierOne(t)));
      all('[data-plan-card]').forEach(function (card) {
        var on = card.getAttribute('data-plan-card') === (sub ? 'sub' : 'one');
        card.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      all('[data-sticky-plan]').forEach(function (b) {
        b.setAttribute('aria-pressed', b.getAttribute('data-sticky-plan') === (sub ? 'sub' : 'one') ? 'true' : 'false');
      });

      /* hidden form inputs */
      var spIn = box.querySelector('[data-sub-input]');
      if (spIn) {
        if (sub && p) { spIn.disabled = false; spIn.value = p.id; }
        else { spIn.disabled = true; spIn.value = ''; }
      }
      var qIn = box.querySelector('[data-sub-qty]');
      if (qIn) qIn.value = t;

      /* add to cart */
      var label = !avail ? (cfg.labels.soldOut || 'Sold out') : (sub ? (cfg.labels.subscribeNow || 'Subscribe now') : (cfg.labels.addToBag || 'Add to bag'));
      setText('[data-atc-label]', label);
      setText('[data-atc-price]', avail ? fmt(price) : '');
      all('[data-sub-atc]').forEach(function (b) { b.disabled = !avail; });
      all('.bsfn-sub__dash').forEach(function (d) { d.hidden = !avail; });

      /* copy with tokens */
      all('[data-sub-copy]').forEach(fill);
      all('[data-sub-note]').forEach(function (el) {
        var tpl = sub ? (cfg.labels.subNote || '') : (hasPlans() ? (cfg.labels.oneNote || '') : '');
        el.setAttribute('data-tpl', tpl);
        fill(el);
      });

      /* sticky summary */
      var sum = t + ' ' + unitWord(t) + ' · ' + (sub
        ? (cfg.labels.subTitle || 'Subscribe & Save') + ', ' + freqPhrase(p ? p.name : '')
        : (cfg.labels.oneTitle || 'One-time purchase'));
      setText('[data-sticky-summary]', sum);

      updateSticky();
    }

    /* ---------- sticky bar visibility ---------------------------------- */
    function updateSticky() {
      if (!sticky) return;
      var on = !!cfg.sticky && state.pastAtc && !state.cartOpen;
      sticky.classList.toggle('is-on', on);
      sticky.setAttribute('aria-hidden', on ? 'false' : 'true');
    }
    if (sticky) {
      sticky.hidden = false;
      var mainAtc = box.querySelector('[data-sub-atc]');
      if (mainAtc && 'IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          var e = entries[0];
          state.pastAtc = !e.isIntersecting && e.boundingClientRect.top < 0;
          updateSticky();
        }, { threshold: 0 }).observe(mainAtc);
      }
      document.addEventListener('bsfn:drawer:opened', function () { state.cartOpen = true; updateSticky(); });
      document.addEventListener('bsfn:drawer:closed', function () { state.cartOpen = false; updateSticky(); });
    }

    /* ---------- interactions ------------------------------------------- */
    function setPlan(plan) {
      if (plan === 'one' && cfg.requiresPlan) return;
      if (plan === 'sub' && !hasPlans()) return;
      state.plan = plan;
      render();
      var p = currentPlan();
      emit('bsfn:purchase_option', { plan: isSub() ? 'sub' : 'one', sellingPlanId: isSub() && p ? p.id : null });
    }
    function setTier(t) {
      state.tier = t;
      render();
      emit('bsfn:tier', { tier: t });
    }

    scope.addEventListener('click', function (e) {
      var tierBtn = e.target.closest('[data-tier]');
      if (tierBtn && box.contains(tierBtn)) { setTier(parseInt(tierBtn.getAttribute('data-tier'), 10)); return; }

      var stick = e.target.closest('[data-sticky-plan]');
      if (stick) { setPlan(stick.getAttribute('data-sticky-plan')); return; }

      var card = e.target.closest('[data-plan-card]');
      if (card && box.contains(card)) {
        if (e.target.closest('select,option,label.bsfn-sub__freq')) return;   // the frequency picker handles itself
        setPlan(card.getAttribute('data-plan-card'));
      }
    });

    box.addEventListener('keydown', function (e) {
      var card = e.target.closest('[data-plan-card]');
      if (!card || e.target !== card) return;
      if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setPlan(card.getAttribute('data-plan-card')); }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        var other = card.getAttribute('data-plan-card') === 'sub' ? 'one' : 'sub';
        setPlan(other);
        var el = box.querySelector('[data-plan-card="' + other + '"]');
        if (el) el.focus();
      }
    });

    box.addEventListener('change', function (e) {
      if (e.target.matches('[data-sub-freq]')) {
        state.planId = e.target.value;
        if (!isSub()) state.plan = 'sub';
        render();
        var p = currentPlan();
        emit('bsfn:purchase_option', { plan: 'sub', sellingPlanId: p ? p.id : null });
      }
    });

    if (form) {
      form.addEventListener('change', function (e) {
        if (e.target.name === 'id') {
          state.variantId = currentVariantId();
          /* Keep the shopper's frequency when the new variant offers it;
             otherwise go back to the box's default plan. */
          var keep = plans().some(function (p) { return String(p.id) === String(state.planId); });
          if (!keep) state.planId = cfg.defaultPlanId || null;
          rebuildFreq();
          render();
        }
        if (!tiersOn && e.target.name === 'quantity') {
          state.tier = Math.max(1, parseInt(e.target.value, 10) || 1);
          render();
        }
      });
      if (!tiersOn) {
        form.addEventListener('input', function (e) {
          if (e.target.name === 'quantity') { state.tier = Math.max(1, parseInt(e.target.value, 10) || 1); render(); }
        });
      }
    }

    /* ---------- FAQ accordion ------------------------------------------ */
    if (faq) {
      faq.addEventListener('click', function (e) {
        var q = e.target.closest('[data-faq-q]');
        if (!q) return;
        var open = q.getAttribute('aria-expanded') === 'true';
        faq.querySelectorAll('[data-faq-q]').forEach(function (b) {
          b.setAttribute('aria-expanded', 'false');
          var a = document.getElementById(b.getAttribute('aria-controls'));
          if (a) a.hidden = true;
        });
        if (!open) {
          q.setAttribute('aria-expanded', 'true');
          var ans = document.getElementById(q.getAttribute('aria-controls'));
          if (ans) ans.hidden = false;
        }
      });
    }

    rebuildFreq();
    render();
  }

  /* ---------- cart: switch a one-time line onto a selling plan ------------- */

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-bsfn-switch-plan]');
    if (!btn) return;
    e.preventDefault();
    if (btn.disabled) return;
    btn.disabled = true;
    var was = btn.textContent;
    btn.textContent = '…';
    var inDrawer = !!btn.closest('[data-bsfn-cart-drawer]');
    var keyEl = btn.closest('[data-key]');
    var ev = BSFN.std && keyEl
      ? BSFN.std.cartLines(btn, 'update', inDrawer ? 'dialog' : 'cart',
          [{ id: keyEl.getAttribute('data-key'), quantity: parseInt(btn.getAttribute('data-quantity'), 10) || 1 }])
      : { done: function () {}, fail: function () {} };
    var body = {
      line: parseInt(btn.getAttribute('data-line'), 10),
      quantity: parseInt(btn.getAttribute('data-quantity'), 10) || 1,
      selling_plan: parseInt(btn.getAttribute('data-bsfn-switch-plan'), 10)
    };
    fetch(ROOT + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { if (!r.ok) throw new Error('change failed'); return r.json(); })
      .then(function (cart) {
        ev.done(cart);
        if (inDrawer) emit('bsfn:cart:updated', { cart: cart, source: 'switch' });
        else window.location.reload();
      })
      .catch(function (err) {
        ev.fail(err);
        btn.disabled = false; btn.textContent = was;
        window.location.href = ROOT + 'cart';
      });
  });

  /* ---------- boot --------------------------------------------------------- */

  function init(root) {
    Array.prototype.forEach.call((root || document).querySelectorAll('[data-bsfn-sub]'), initBox);
    dates(root || document);
  }

  BSFN.sub = { init: init, dates: dates, money: money, nextDate: nextDate, freqPhrase: freqPhrase };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();

  /* The theme editor re-renders a section when its settings change. */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
